import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  getBotCommandsAPI,
  getRobotGroupsAPI,
  getCalendarDataAPI,
  updateCalendarAPI,
  deleteCalendarAPI,
  saveCaldendarDataAPI,
} from '../api/calendar';
import {
  REGISTER_TOGGLE_ALL,
  REPEATVALUE_DAY_OPTIONS,
  REPEAT_OPTION_NONE,
} from '../common/consts';
import {
  arrayUnique,
  getManagerRobotId,
  getRobotIds,
  getUserId,
  getUserPId,
} from '../common/util';

const MODULE_CALENDAR = 'calendarBot';

const TODAY = moment().set({ second: 0, millisecond: 0 });
const MODAL_INITIAL = {
  open: false,
  loading: false,
  type: null,
  error: null,
};
const PROCESS_INITIAL = { pending: true, result: null, message: null };
const REGISTER_INITIAL = {
  result: null,
  pending: false,
  message: null,
  today: TODAY,
  error: null,
  cmds: [],
  groups: [],
  group: { type: REGISTER_TOGGLE_ALL, list: [] },
  schedule: {
    bot: null,
    command: false,
    execTime: TODAY,
    title: '',
    repeat: false,
    repeatValue: {
      type: REPEAT_OPTION_NONE,
      value: {
        day: [...REPEATVALUE_DAY_OPTIONS],
        date: TODAY.get('date'),
        month: TODAY.get('month'),
      },
      times: 1,
    },
  },
};
const initialState = {
  pending: false,
  robotData: {},
  calendarDate: [],
  error: null,
  selectDate: moment(),
  groupModal: { ...MODAL_INITIAL },
  modal: { ...MODAL_INITIAL },
  update: { ...PROCESS_INITIAL, pending: false },
  delete: { ...PROCESS_INITIAL, pending: false },
  register: { ...REGISTER_INITIAL },
};

export const getData = createAsyncThunk(
  `${MODULE_CALENDAR}/getData`,
  async (selectDate, { rejectWithValue, getState }) => {
    try {
      const { result, calData } = await getCalendarDataAPI({
        userId: getUserPId(),
        date: selectDate.format('YYYY-MM'),
        robotId: getManagerRobotId(),
      });
      if (result) {
        const { robotData: oldRobotData } = getState().calendarBot;
        const thisMoment = moment(selectDate.format('YYYY-MM-DD'));
        const monthDateLength = thisMoment.endOf('month').get('date');
        const datesArr = Array.from({ length: monthDateLength });
        const robotData = {};
        const calendarData = datesArr.map((_v, i) => {
          const arr = [];
          const tmp = [];
          calData.forEach(({ robot: { id: robotId, ...robot }, data }, idx) => {
            if (!(robot.id in robotData)) {
              if (
                robotId in oldRobotData &&
                'checked' in oldRobotData[robotId]
              ) {
                robotData[robotId] = {
                  ...robot,
                  checked: oldRobotData[robotId].checked || false,
                };
              } else {
                robotData[robotId] = {
                  ...robot,
                  checked: true,
                };
              }
            }
            if (data[i].length > 0) {
              if (arr.findIndex((value) => value.id === robotId) < 0) {
                const list = tmp
                  .concat(data[i])
                  .sort(({ execTime: a }, { execTime: b }) =>
                    moment(a).isAfter(b) ? 1 : -1,
                  );
                arr.push({
                  ...{ ...robot, id: robotId },
                  list: arrayUnique(list, '_id'),
                  className: `bg-${idx % 30}`,
                });
              }
            }
          });
          return arr;
        });

        return { calendarData, robotData, selectDate };
      }
      return rejectWithValue('일정 목록 로드 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('일정 목록 로드 중 오류가 발생했습니다.');
    }
  },
);

export const getCommands = createAsyncThunk(
  `${MODULE_CALENDAR}/cmds`,
  async (_undefined, { rejectWithValue }) => {
    try {
      const robots = getRobotIds();
      const { result, list } = await getBotCommandsAPI({ robots });
      if (result) {
        return list;
      }
      return rejectWithValue('명령어 정보 로드 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('명령어 정보 로드 중 오류가 발생했습니다.');
    }
  },
);

export const openUpdateModal = createAsyncThunk(
  `${MODULE_CALENDAR}/modal`,
  async ({ type, list }, { rejectWithValue }) => {
    try {
      const [cmdResult, groupResult] = await Promise.all([
        getBotCommandsAPI({ robots: getRobotIds() }),
        getRobotGroupsAPI({ userId: getUserPId() }),
      ]);
      if (cmdResult.result && groupResult.result) {
        // return {
        //   items: { cmds: cmdResult.list, groupLists: groupResult.list },
        // };
        return {
          type,
          items: { list, cmds: cmdResult.list, groupLists: groupResult.list },
        };
      }
      return rejectWithValue(
        '일정 수정에 필요한 정보 로드 중 오류가 발생했습니다.',
      );
    } catch (error) {
      return rejectWithValue(
        '일정 수정에 필요한 정보 로드 중 오류가 발생했습니다.',
      );
    }
  },
);

export const getGroups = createAsyncThunk(
  `${MODULE_CALENDAR}/getGroups`,
  async (_undefined, { rejectWithValue }) => {
    try {
      const { result, list } = await getRobotGroupsAPI({
        userId: getUserPId(),
      });
      if (result) {
        return list;
      }
      return rejectWithValue(
        '로봇 그룹 선택 팝업 로드 중 오류가 발생했습니다.',
      );
    } catch (error) {
      return rejectWithValue(
        '로봇 그룹 선택 팝업 로드 중 오류가 발생했습니다.',
      );
    }
  },
);

export const updateItem = createAsyncThunk(
  `${MODULE_CALENDAR}/update`,
  async ({ list }, { rejectWithValue }) => {
    try {
      const { result } = await updateCalendarAPI({
        userId: getUserPId(),
        list,
      });
      if (result) {
        return '일정 수정이 완료되었습니다.';
      }
      return rejectWithValue('일정 수정 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('일정 수정 중 오류가 발생했습니다.');
    }
  },
);

export const deleteItem = createAsyncThunk(
  `${MODULE_CALENDAR}/delete`,
  async ({ list }, { rejectWithValue }) => {
    try {
      const { result } = await deleteCalendarAPI({
        userId: getUserId(),
        list,
      });
      if (result) {
        return '일정 삭제가 완료되었습니다.';
      }
      return rejectWithValue('일정 삭제 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('일정 삭제 중 오류가 발생했습니다.');
    }
  },
);

export const saveItem = createAsyncThunk(
  `${MODULE_CALENDAR}/save`,
  async ({ robotIds, data, group }, { rejectWithValue }) => {
    try {
      const { result } = await saveCaldendarDataAPI({
        userId: getUserPId(),
        robotIds,
        data,
        group,
      });
      if (result) {
        return '일정 저장에 성공하였습니다.';
      }
      return rejectWithValue('일정 저장 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('일정 저장 중 오류가 발생했습니다.');
    }
  },
);

const toggleGroupReducer = (state, action) => {
  const { robotData } = state;
  const { id, checked } = action.payload;
  robotData[id].checked = checked;
};

const calendarSlice = createSlice({
  name: MODULE_CALENDAR,
  initialState,
  reducers: {
    setDate: (state, action) => ({ ...state, selectDate: action.payload }),
    toggleGroup: toggleGroupReducer,
    registerInit: (state) => ({
      ...state,
      register: {
        ...REGISTER_INITIAL,
        today: moment().set({ second: 0, millisecond: 0 }),
        schedule: {
          ...REGISTER_INITIAL.schedule,
          execTime: moment().set({ second: 0, millisecond: 0 }),
        },
      },
    }),
    openModal: (state, action) => ({
      ...state,
      groupModal: {
        open: true,
        loading: false,
        items: { groupLists: action.payload },
      },
    }),
    closeModal: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL },
      groupModal: { ...MODAL_INITIAL },
    }),
    setSchedule: (state, action) => ({
      ...state,
      register: { ...state.register, ...action.payload },
    }),
    setScheduleError: (state, action) => ({
      ...state,
      register: { ...state.register, error: action.payload },
    }),
  },
  extraReducers: {
    [getData.pending.type]: (state) => ({ ...state, pending: true }),
    [getData.fulfilled.type]: (state, action) => ({
      ...state,
      pending: false,
      ...action.payload,
    }),
    [getData.rejected.type]: (state, action) => ({
      ...state,
      pending: false,
      error: action.payload,
    }),
    [getCommands.fulfilled.type]: (state, action) => ({
      ...state,
      register: { ...state.register, cmds: action.payload },
    }),
    [getCommands.rejected.type]: (state, action) => ({
      ...state,
      register: {
        ...state.register,
        error: { ...state.error, cmds: action.payload },
      },
    }),
    [getGroups.fulfilled.type]: (state, action) => ({
      ...state,
      register: {
        ...state.register,
        groups: action.payload,
      },
    }),
    [getGroups.rejected.type]: (state, action) => ({
      ...state,
      register: {
        ...state.register,
        error: { ...state.error, groups: action.payload },
      },
    }),
    [openUpdateModal.pending.type]: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL, loading: true },
    }),
    [openUpdateModal.fulfilled.type]: (state, action) => ({
      ...state,
      modal: { open: true, loading: false, ...action.payload },
    }),
    [openUpdateModal.rejected.type]: (state, action) => ({
      ...state,
      modal: { error: action.payload },
    }),
    [updateItem.pending.type]: (state) => ({
      ...state,
      update: { ...PROCESS_INITIAL },
    }),
    [updateItem.fulfilled.type]: (state, action) => ({
      ...state,
      update: { pending: false, result: true, message: action.payload },
    }),
    [updateItem.rejected.type]: (state, action) => ({
      ...state,
      update: { pending: false, result: false, error: action.payload },
    }),
    [deleteItem.pending.type]: (state) => ({
      ...state,
      delete: { ...PROCESS_INITIAL },
    }),
    [deleteItem.fulfilled.type]: (state, action) => ({
      ...state,
      delete: { pending: false, result: true, message: action.payload },
    }),
    [deleteItem.rejected.type]: (state, action) => ({
      ...state,
      delete: { pending: false, result: false, error: action.payload },
    }),
    [saveItem.pending.type]: (state) => ({
      ...state,
      register: { ...state.register, pending: true },
    }),
    [saveItem.fulfilled.type]: (state, action) => ({
      ...state,
      register: {
        ...REGISTER_INITIAL,
        groups: state.register.groups,
        result: true,
        message: action.payload,
      },
    }),
    [saveItem.rejected.type]: (state, action) => ({
      ...state,
      register: {
        ...state.register,
        pending: false,
        result: false,
        message: action.payload,
      },
    }),
  },
});

const { reducer: CalendarReducer, actions } = calendarSlice;
export const {
  setDate,
  toggleGroup,
  openModal,
  closeModal,
  setSchedule,
  setScheduleError,
  registerInit,
} = actions;

export default CalendarReducer;
